// The demo set: the approved design's pilot example (Express Delivery, Bastos,
// Essos, Melen, Mendong, 38 connected homes), in the exact shapes the ops_*
// reads return. Shown only behind the "Démo" pill, never mixed with real data.
import type { Alert, Live, OpsOrder, OrderStatus, OrderType, Overview } from './types';
// demo-homes imports this file too; both only call each other inside functions, so the cycle is safe.
import { demoHomes } from './demo-homes';
import { centreOf } from './zones';

const MIN = 60_000;
const at = (minAgo: number) => new Date(Date.now() - minAgo * MIN).toISOString();

export const AGENCY = { id: 'demo-agency', name: 'Express Delivery' };
// The catalogue's codes and bottle paints (migration 0048), with the name on the bottle.
export const BRANDS = {
  afrigaz: { code: 'AFRIGAZ', name: 'AfriGaz', brand_color: '#C4E6E2' },
  tradex: { code: 'TRADEX', name: 'Tradex', brand_color: '#1E3CCB' },
  total: { code: 'TOTAL', name: 'TotalEnergies', brand_color: '#0D2160' },
  bocom: { code: 'BOCOM', name: 'Bocom', brand_color: '#1E8E4E' },
};
const RIDERS = {
  r1: { id: 'r1', name: 'Samuel Ndzi', plate: 'CE 482 KL', rating: 4.9, phone: '+237 670 00 00 01' },
  r2: { id: 'r2', name: 'Marcel Onana', plate: 'CE 117 AB', rating: 4.8, phone: '+237 670 00 00 02' },
  r4: { id: 'r4', name: 'Jean-Paul Mballa', plate: 'CE 903 MX', rating: 4.7, phone: '+237 670 00 00 04' },
  r5: { id: 'r5', name: 'Hervé Tchoumi', plate: 'CE 256 PQ', rating: 4.6, phone: '+237 670 00 00 05' },
};

interface Seed {
  ref: string; name: string; q: string; type: OrderType; status: OrderStatus; brand: keyof typeof BRANDS;
  rider?: keyof typeof RIDERS; created: number; steps: [OrderStatus, number][]; fee?: number; level?: number; fail?: string;
}

const SEEDS: Seed[] = [
  { ref: 'MG-90AA4C', name: 'Christine Abena', q: 'Bastos', type: 'refill', status: 'placed', brand: 'afrigaz', created: 6, steps: [['placed', 6]], level: 9 },
  { ref: 'MG-77D3E1', name: 'Sylvie Ngo Bell', q: 'Melen', type: 'refill', status: 'agent_en_route', brand: 'tradex', rider: 'r5', created: 38, steps: [['placed', 38], ['assigned', 33], ['agent_en_route', 21]], fee: 1500, level: 6 },
  { ref: 'MG-3B81F0', name: 'Paul Etoga', q: 'Essos', type: 'refill', status: 'tech_arrived', brand: 'total', rider: 'r1', created: 44, steps: [['placed', 44], ['assigned', 41], ['agent_en_route', 30], ['tech_arrived', 2]], level: 14 },
  { ref: 'MG-C20E57', name: 'Brigitte Fouda', q: 'Bastos', type: 'first_install', status: 'install_in_progress', brand: 'afrigaz', rider: 'r2', created: 95, steps: [['placed', 95], ['assigned', 80], ['agent_en_route', 52], ['tech_arrived', 31], ['install_in_progress', 24]] },
  { ref: 'MG-5A1D9B', name: 'Emmanuel Nkoulou', q: 'Mendong', type: 'refill', status: 'completed', brand: 'bocom', rider: 'r4', created: 160, steps: [['placed', 160], ['assigned', 155], ['agent_en_route', 140], ['tech_arrived', 118], ['completed', 112]] },
  { ref: 'MG-E44C02', name: 'Rose Mbarga', q: 'Essos', type: 'refill', status: 'completed', brand: 'tradex', rider: 'r1', created: 210, steps: [['placed', 210], ['assigned', 206], ['agent_en_route', 195], ['tech_arrived', 178], ['completed', 171]] },
  { ref: 'MG-1F6A38', name: 'Didier Owona', q: 'Melen', type: 'refill', status: 'completed', brand: 'afrigaz', rider: 'r5', created: 260, steps: [['placed', 260], ['assigned', 257], ['agent_en_route', 247], ['tech_arrived', 229], ['completed', 224]] },
  { ref: 'MG-8D27B6', name: 'Laure Ekambi', q: 'Bastos', type: 'refill', status: 'cancelled_by_customer', brand: 'total', created: 300, steps: [['placed', 300], ['cancelled_by_customer', 296]] },
  { ref: 'MG-4F2K9A', name: 'Gisèle Manga', q: 'Mendong', type: 'first_install', status: 'failed_first_install', brand: 'bocom', rider: 'r2', created: 1460, steps: [['placed', 1460], ['assigned', 1440], ['agent_en_route', 1320], ['tech_arrived', 1290], ['failed_first_install', 1262]], fail: 'Cliente absente, deux appels sans réponse. Le kit est revenu au dépôt.' },
  { ref: 'MG-B9037E', name: 'Félicité Ondoa', q: 'Essos', type: 'first_install', status: 'install_completed', brand: 'tradex', rider: 'r2', created: 1520, steps: [['placed', 1520], ['assigned', 1500], ['agent_en_route', 1400], ['tech_arrived', 1370], ['install_in_progress', 1365], ['install_completed', 1325]] },
];

function order(s: Seed): OpsOrder {
  const inst = s.type === 'first_install';
  const events: OpsOrder['events'] = {};
  s.steps.forEach(([st, m]) => { events[st] = at(m); });
  const fee = s.fee ?? 1200;
  return {
    id: 'demo-' + s.ref, ref: s.ref, order_type: s.type, status: s.status, created_at: at(s.created), events,
    customer: { name: s.name, quartier: s.q, phone: '+237 690 00 00 00' }, customer_id: 'hh-' + s.ref,
    station: { name: s.q === 'Essos' ? 'Relais Essos' : 'Dépôt Express Delivery' }, marketer: BRANDS[s.brand],
    size_kg: 12.5, gas_price: 6500, delivery_fee: inst ? 0 : fee, fare: inst ? 0 : fee, currency: 'XAF',
    eta_at: s.status === 'agent_en_route' ? at(-9) : null, distance_m: 3400, failure_reason: s.fail ? 'customer_absent' : null,
    failure_note: s.fail ?? null, rating: s.status === 'completed' ? 5 : null, visit: inst ? { from: at(120), to: at(-120) } : null,
    waiting_for_empty: false, kit: inst ? { price: 9000, currency: 'XAF', paid: true } : null, agency: AGENCY,
    rider: s.rider ? RIDERS[s.rider] : null,
    home: s.level == null ? null : { level: s.level, measured_at: at(14), received_at: at(14), online: true, gateway: 'UC-0B4' + s.ref.slice(-1) },
    log: s.steps.map(([st, m]) => ({ status: st, at: at(m), actor: st === 'placed' || st === 'cancelled_by_customer' ? 'customer' : 'rider' })),
  };
}

export const demoOrders = (): OpsOrder[] => SEEDS.map(order);

export function demoOverview(): Overview {
  const os = demoOrders();
  const by = (r: string) => os.find((o) => o.ref === r)!;
  const today = os.filter((o) => Date.now() - new Date(o.created_at).getTime() < 14 * 60 * MIN);
  const alerts: Alert[] = [
    { kind: 'unassigned', order: by('MG-90AA4C') },
    { kind: 'failed_install', order: by('MG-4F2K9A') },
    { kind: 'silent_gateway', home: { customer_id: 'h14', name: 'Yvette Nnanga', quartier: 'Melen', gateway: 'UC-0B4E', received_at: at(41 * 60) } },
    { kind: 'low_without_order', homes: [{ customer_id: 'h8', name: 'Danielle Ateba', quartier: 'Essos', level: 13 }, { customer_id: 'h11', name: 'Irène Mvogo', quartier: 'Bastos', level: 17 }] },
  ];
  const days = [6, 8, 5, 9, 11, 7, 8, 10, 9, 12, 8, 11, 9, today.filter((o) => o.status === 'completed' || o.status === 'install_completed').length];
  return {
    generated_at: new Date().toISOString(),
    today: { orders: today.length, delivered: days[13] },
    days14: days.map((d, i) => ({ day: new Date(Date.now() - (13 - i) * 86400000).toISOString().slice(0, 10), delivered: d })),
    orders14: [7, 9, 6, 10, 12, 8, 9, 11, 10, 13, 9, 12, 10, today.length].map((n, i) => ({ day: new Date(Date.now() - (13 - i) * 86400000).toISOString().slice(0, 10), orders: n })),
    homes: { connected: 38, low: 6, low_no_order: 2, online: 37 },
    riders: { live: 5, en_route: 1, at_door: 2 },
    alerts,
    soon_empty: [
      { customer_id: 'hh-MG-77D3E1', name: 'Sylvie Ngo Bell', quartier: 'Melen', level: 6, measured_at: at(14), marketer: BRANDS.tradex, open_order: by('MG-77D3E1') },
      { customer_id: 'hh-MG-90AA4C', name: 'Christine Abena', quartier: 'Bastos', level: 9, measured_at: at(20), marketer: BRANDS.afrigaz, open_order: by('MG-90AA4C') },
      { customer_id: 'h8', name: 'Danielle Ateba', quartier: 'Essos', level: 13, measured_at: at(31), marketer: BRANDS.total, open_order: null },
      { customer_id: 'hh-MG-3B81F0', name: 'Paul Etoga', quartier: 'Essos', level: 14, measured_at: at(9), marketer: BRANDS.total, open_order: by('MG-3B81F0') },
      { customer_id: 'h11', name: 'Irène Mvogo', quartier: 'Bastos', level: 17, measured_at: at(45), marketer: BRANDS.afrigaz, open_order: null },
    ],
  };
}

export function demoLive(): Live {
  const os = demoOrders();
  const o = (r: string) => os.find((x) => x.ref === r)!;
  const brief = (x: OpsOrder) => ({ id: x.id, ref: x.ref, status: x.status, customer: x.customer.name, quartier: x.customer.quartier });
  return {
    riders: [
      { id: 'r5', name: 'Hervé Tchoumi', agency: AGENCY.name, live: true, lat: 3.8668, lng: 11.4991, at: at(0.2), heading: 250, order: brief(o('MG-77D3E1')) },
      { id: 'r1', name: 'Samuel Ndzi', agency: AGENCY.name, live: true, lat: 3.8731, lng: 11.5368, at: at(0.1), heading: null, order: brief(o('MG-3B81F0')) },
      { id: 'r2', name: 'Marcel Onana', agency: AGENCY.name, live: true, lat: 3.8948, lng: 11.5139, at: at(0.3), heading: null, order: brief(o('MG-C20E57')) },
      { id: 'r4', name: 'Jean-Paul Mballa', agency: AGENCY.name, live: true, lat: 3.8562, lng: 11.5221, at: at(0.2), heading: 40, order: null },
      { id: 'r6', name: 'Alain Biyong', agency: AGENCY.name, live: true, lat: 3.8410, lng: 11.4900, at: at(0.5), heading: null, order: null },
    ],
    homes: [
      { order_id: o('MG-90AA4C').id, status: 'placed', customer: 'Christine Abena', quartier: 'Bastos', lat: 3.8972, lng: 11.5098 },
      { order_id: o('MG-77D3E1').id, status: 'agent_en_route', customer: 'Sylvie Ngo Bell', quartier: 'Melen', lat: 3.8624, lng: 11.4936 },
      { order_id: o('MG-3B81F0').id, status: 'tech_arrived', customer: 'Paul Etoga', quartier: 'Essos', lat: 3.8733, lng: 11.5372 },
      { order_id: o('MG-C20E57').id, status: 'install_in_progress', customer: 'Brigitte Fouda', quartier: 'Bastos', lat: 3.8950, lng: 11.5142 },
    ],
    stations: [
      { id: 's1', name: 'Dépôt Express Delivery', lat: 3.8590, lng: 11.5170 },
      { id: 's2', name: 'Relais Essos', lat: 3.8760, lng: 11.5330 },
    ],
    zones: ['Bastos', 'Essos', 'Melen', 'Mendong'],
    map: demoMap(),
  };
}

// Every demo home placed near its quartier's centre (a fixed scatter, so the
// preview looks the same each time), and a little stock at the two stations.
function demoMap(): NonNullable<Live['map']> {
  const homes = demoHomes().flatMap((h, i) => {
    const c = centreOf(h.quartier);
    if (!c) return [];
    const a = i * 2.39996, r = 0.0018 + (i % 5) * 0.0011;
    return [{ ...h, lat: c[1] + Math.sin(a) * r, lng: c[0] + Math.cos(a) * r }];
  });
  const stock = [
    ['s1', BRANDS.tradex, 4], ['s1', BRANDS.total, 6], ['s1', BRANDS.bocom, 1], ['s2', BRANDS.afrigaz, 2], ['s2', BRANDS.total, 3],
  ] as const;
  return { homes, stock: stock.map(([id, b, n]) => ({ station_id: id, code: b.code, name: b.name, brand_color: b.brand_color, size_kg: 12.5, full: n })) };
}
